/**
 * VenueContactPills — verified-only contact channels for a venue
 *
 * Founder ear-test 6 Jun 2026 evening (CinCin screenshot):
 *   "Only show a contact method we've actually VERIFIED for that
 *   venue (real phone, real site, real email from the verified
 *   record) — tagged with its source, same as everywhere. No
 *   guessed emails, no [email protected] patterns. If we don't
 *   have a verified contact, that pill simply doesn't show for
 *   that venue — honest blank, never a fabricated contact."
 *
 * What we surface (in priority order):
 *   1. WhatsApp · only when `whatsapp_active` AND a number exists ·
 *      pre-filled with "Hi, I'd like to reserve a table at {name}".
 *   2. Call · when `phone_active` AND `phone` exists · `tel:` URL.
 *   3. Email · only when `email_active` AND `email` is present ·
 *      `mailto:` with a clear subject + body. We never invent
 *      an email from a domain.
 *   4. Website · when `website_active` AND `website` exists ·
 *      opens in new tab.
 *
 * Doctrine:
 *   - Each pill carries a "· Google Places" / "· archive" provenance
 *     tag so the founder/client sees WHERE we learned the contact.
 *   - When none of the pills can honestly show, we render NOTHING.
 *   - We never auto-fabricate a WhatsApp number from `phone` ·
 *     `whatsapp_active` is a separate, deliberate verification.
 */
import React from "react";


function normalisePhone(raw) {
    if (!raw) return null;
    // Strip everything that isn't a digit or +
    const trimmed = String(raw).replace(/[^\d+]/g, "");
    if (!trimmed) return null;
    return trimmed;
}


function buildBookingText(venueName, ctx = {}) {
    const lines = [];
    lines.push(`Hi · I'd like to enquire about a reservation at ${venueName}.`);
    if (ctx.date) lines.push(`Date: ${ctx.date}`);
    if (ctx.time) lines.push(`Time: ${ctx.time}`);
    if (ctx.guests) lines.push(`Guests: ${ctx.guests}`);
    if (ctx.guestName) lines.push(`Name: ${ctx.guestName}`);
    lines.push("");
    lines.push("Thank you.");
    return lines.join("\n");
}


export function VenueContactPills({
    venue,
    // Optional booking context · drawer pre-fills with date/time/guests
    bookingContext = null,
    // "compact" hides labels (used inside dense cards)
    compact = false,
    // testid prefix · enables card vs drawer differentiation in tests
    testIdPrefix = "venue-contact",
}) {
    if (!venue) return null;

    const venueName = venue.name || "the restaurant";

    const phoneRaw = venue.phone;
    const phoneActive = Boolean(venue.phone_active);
    const phoneCleaned = phoneActive ? normalisePhone(phoneRaw) : null;

    const waNumber = venue.whatsapp || phoneRaw;
    const waActive = Boolean(venue.whatsapp_active);
    const waCleaned = waActive ? normalisePhone(waNumber) : null;

    const email = venue.email;
    const emailActive = Boolean(venue.email_active);
    const showEmail = emailActive && email && email.includes("@");

    const websiteRaw = venue.website;
    const websiteActive = Boolean(venue.website_active);
    const websiteHref = websiteActive && websiteRaw ? websiteRaw : null;

    const anyPill = waCleaned || phoneCleaned || showEmail || websiteHref;
    if (!anyPill) return null;

    const bookingText = buildBookingText(venueName, bookingContext || {});
    const bookingTextEncoded = encodeURIComponent(bookingText);

    const emailSubject = encodeURIComponent(
        `Reservation enquiry · ${venueName}`,
    );

    // Provenance · Places venues vs archive venues
    const placesProvenance = venue.evidence_source?.kind === "lc_archive"
        ? "archive" : "live";

    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid={testIdPrefix}
            style={{ marginTop: compact ? 6 : 10 }}
        >
            {waCleaned ? (
                <a
                    href={`https://wa.me/${waCleaned.replace(/^\+/, "")}?text=${bookingTextEncoded}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-whatsapp`}
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9.5,
                        letterSpacing: "0.14em",
                        color: "#0E7C50",
                        background: "rgba(14, 124, 80, 0.08)",
                        border: "1px solid rgba(14, 124, 80, 0.35)",
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontWeight: 700,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {compact ? "wa" : "WhatsApp"}
                    {/* K1.2: card mode (compact) drops the
                        "· per the venue" provenance suffix. */}
                    {!compact ? (
                        <span
                            style={{
                                fontWeight: 400, color: "#0E7C50CC",
                                marginLeft: 5, letterSpacing: "0.10em",
                            }}
                        >
                            · per the venue
                        </span>
                    ) : null}
                </a>
            ) : null}

            {phoneCleaned ? (
                <a
                    href={`tel:${phoneCleaned}`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-call`}
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9.5,
                        letterSpacing: "0.14em",
                        color: "var(--t-ink)",
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontWeight: 700,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {compact ? "call" : `Call ${phoneRaw}`}
                    {/* K1.2 (14 Jun 2026 evening): card mode (compact)
                        drops the "· Google" / "· archive" provenance
                        suffix · the drawer keeps it. */}
                    {!compact ? (
                        <span
                            style={{
                                fontWeight: 400, color: "var(--t-ink3)",
                                marginLeft: 5, letterSpacing: "0.10em",
                            }}
                        >
                            · {placesProvenance}
                        </span>
                    ) : null}
                </a>
            ) : null}

            {showEmail ? (
                <a
                    href={`mailto:${email}?subject=${emailSubject}&body=${bookingTextEncoded}`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-email`}
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9.5,
                        letterSpacing: "0.14em",
                        color: "var(--t-ink)",
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontWeight: 700,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {compact ? "email" : "Email"}
                    {/* K1.2: card mode drops the "· per the venue" suffix. */}
                    {!compact ? (
                        <span
                            style={{
                                fontWeight: 400, color: "var(--t-ink3)",
                                marginLeft: 5, letterSpacing: "0.10em",
                            }}
                        >
                            · per the venue
                        </span>
                    ) : null}
                </a>
            ) : null}

            {websiteHref ? (
                <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-website`}
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9.5,
                        letterSpacing: "0.14em",
                        color: "var(--t-brass)",
                        background: "var(--t-brass-wash)",
                        border: "1px solid var(--t-brass)",
                        borderRadius: 999,
                        padding: "5px 11px",
                        fontWeight: 700,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {compact ? "site \u2197" : "Site \u2197"}
                </a>
            ) : null}
        </div>
    );
}


export default VenueContactPills;
